import { useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { saveSalesperson } from '@/services/salespersons';

interface SalespersonModalProps {
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = {
  fullName: '',
  email: '',
  phone: '',
  commissionRate: '5',
  isActive: true,
};

export default function SalespersonModal({ onClose, onSaved }: SalespersonModalProps) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof typeof emptyForm>(key: K, value: (typeof emptyForm)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.fullName.trim()) {
      setError('El nombre del vendedor es obligatorio.');
      return;
    }

    setSaving(true);
    const { error: saveError } = await saveSalesperson({ ...form, commissionRate: Number(form.commissionRate) || 0 });
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal title="Nuevo Vendedor" subtitle="Registra un agente comercial y su comisión." onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Nombre completo *</label>
          <input className={inputCls} value={form.fullName} onChange={(e) => set('fullName', e.target.value)} placeholder="Laura Martínez" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="laura@peticas.com" />
          </div>
          <div>
            <label className={labelCls}>Teléfono</label>
            <input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+57 3xx xxx xxxx" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Comisión (%)</label>
            <input type="number" min="0" max="100" step="0.5" className={inputCls} value={form.commissionRate} onChange={(e) => set('commissionRate', e.target.value)} />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} className="w-4 h-4 accent-accent-500 cursor-pointer" />
              Activo
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>Crear vendedor</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}